import DownloadIcon from '@apps-common/ui-icons/Download.jsx';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import EditIcon from '@mui/icons-material/Edit';
import {Box, Button, Collapse} from '@mui/material';
import {alpha} from '@mui/system';
import {bool, func, object} from 'prop-types';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import MapPopperButton from '../map-popper-button.jsx';
import {MeasureIndicator} from '../measure-indicator.jsx';
import {aoiTooltipMap} from './tooltips.js';
import {VisibilityControl} from './visibility-control.jsx';
import {MAP_TOOLS} from '@explorer/constants';
import {getExtent} from '@explorer/util/geom';

export const FeatureItem = ({
  onDelete,
  onDownload,
  onSave,
  onEdit,
  onZoomTo,
  isEditing,
  feature,
  tempGeometry,
  canToggleVisibility,
  onChangeVisibility,
  tooltips = aoiTooltipMap,
  onHover,
}) => {
  const {t} = useTranslation();
  const [forceCollapse, setForceCollapse] = useState(false);

  const onClickDelete = useCallback(() => {
    // fold the display then delete.
    setForceCollapse(true);
    setTimeout(() => {
      onDelete(feature);
    }, 300);
  }, [onDelete, feature, setForceCollapse]);

  const onClickSave = useCallback(() => {
    onSave(feature);
  }, [onSave, feature]);

  const onClickEdit = useCallback(() => {
    onEdit(feature);
  }, [onEdit, feature]);

  const onClickVisToggle = useCallback(() => {
    onChangeVisibility(feature);
  }, [onChangeVisibility, feature]);

  const onClickDownload = useCallback(() => {
    onDownload(feature);
  }, [onDownload, feature]);

  // do not trigger enter/leave events
  //  while editing.
  const enterFeature = useCallback(() => {
    if (!isEditing) {
      onHover(feature);
    }
  }, [isEditing, onHover, feature]);

  const leaveFeature = useCallback(() => {
    if (!isEditing) {
      onHover();
    }
  }, [isEditing, onHover]);

  const zoomToFeature = useCallback(() => {
    // get the bounding box for the feature
    if (onZoomTo) {
      const extent = getExtent(feature.geometry);
      onZoomTo([extent.left, extent.bottom, extent.right, extent.top]);
    }
  }, [feature, onZoomTo]);

  return (
    <Collapse in={!forceCollapse && (!!feature || !!tempGeometry)}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        {canToggleVisibility && (
          <VisibilityControl
            visible={feature.properties?.visible !== false}
            isEditing={isEditing}
            onClick={onClickVisToggle}
          />
        )}
        <Box
          sx={theme => ({
            alignItems: 'center',
            backgroundColor:
              feature?.properties?.hover === true || isEditing
                ? alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity
                  )
                : 'rgba(0,0,0,0)',
            borderRadius: theme.spacing(0.25),
            display: 'flex',
            flex: 1,
            height: 56, // height of icon + icon top padding + icon bottom padding
            padding: theme.spacing(1),
            transition: 'background-color 500ms',
          })}
          onMouseEnter={enterFeature}
          onMouseLeave={leaveFeature}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {isEditing && tempGeometry ? (
              <MeasureIndicator
                geometry={tempGeometry}
                onClick={zoomToFeature}
                iconSize="small"
              />
            ) : (
              <MeasureIndicator
                geometry={feature?.geometry}
                onClick={zoomToFeature}
                iconSize="small"
              />
            )}
          </Box>
          {!isEditing && (
            <React.Fragment>
              <MapPopperButton
                tooltip={t('drawTools.buttonDownloadGeojson')}
                onClick={onClickDownload}
                data-qe="download-maptool"
              >
                <DownloadIcon />
              </MapPopperButton>

              <MapPopperButton
                tooltip={t(tooltips[MAP_TOOLS.EDIT])}
                tooltipPosition="top-end"
                onClick={onClickEdit}
              >
                <EditIcon />
              </MapPopperButton>
              <MapPopperButton
                data-qe="clear-aoi"
                onClick={onClickDelete}
                tooltip={t(tooltips.CLEAR)}
                tooltipPosition="top-end"
              >
                <ClearOutlinedIcon />
              </MapPopperButton>
            </React.Fragment>
          )}

          {isEditing && feature && (
            <Button
              variant="contained"
              color="primary"
              size="mini"
              onClick={onClickSave}
            >
              {t('drawTools.buttonSave')}
            </Button>
          )}
        </Box>
      </Box>
    </Collapse>
  );
};

FeatureItem.propTypes = {
  onDelete: func,
  onDownload: func,
  onEdit: func,
  onSave: func,
  onHover: func,
  onZoomTo: func,
  onChangeVisibility: func,
  isEditing: bool,
  canToggleVisibility: bool,
  tooltips: object,
  feature: object,
  tempGeometry: object,
};
